import { BridgeFieldLabels, BridgeType, BridgeTypeFormValues } from "../../../api/Bridges";
import { customImportConstants } from "../../../api/CustomImports";
import { DataSource, DataSourceFilters, DataSourceFieldLabels, DataSourceTypes } from "../../../api/DataSource";
import { DefinedPropertyObject, IEntityMinimalModel } from "../../../api/GenericTypes";
import { SelectFormField } from "../../../common/formfields/SelectFormField";
import { BridgesVirtualizedSelectForm } from "../../../common/forms/EntityForms/formsVirtualized/BridgeVirtualizedSelectForm";
import { CustomImportsVirtualizedSelectForm } from "../../../common/forms/EntityForms/formsVirtualized/CustomImportVirtualizedSelectForm";
import { DataFormatsVirtualizedSelectForm } from "../../../common/forms/EntityForms/formsVirtualized/DataFormatsVirtualizedSelectForm";
import { DatasetsVirtualizedSelectForm } from "../../../common/forms/EntityForms/formsVirtualized/DatasetsVirtualizedSelectForm";
import { toUppercase } from "../../../common/helperfunctions/stringFunctions";
import { EntityFilterSideBar } from "../../../common/sidebar/filtersidebar/EntityFilterBar";
import {
  TabStoreFilterSidebarProps,
  TabStoreFilterFormProvider,
} from "../../../common/sidebar/filtersidebar/TabStoreFilterFormProvider";
import {
  CombinedModificationRecordEntityDefaultFormValues,
  combinedModificationRecordEntityFiltersToForm,
  combinedModificationRecordEntityFormToFilters,
  ICombinedModificationRecordEntityForm,
} from "../../../common/sidebar/filtersidebar/GenericFilters/CombinedModificationRecordEntityFilterFormModels";
import { CombinedModificationRecordEntityFilterForm } from "../../../common/sidebar/filtersidebar/GenericFilters/CombinedModificationRecordEntityFilterForm";

export interface DataSourceFilterForm extends ICombinedModificationRecordEntityForm {
  bridgeIds?: IEntityMinimalModel[] | undefined | null;
  bridgeTypes?: { id: BridgeType; name: string }[] | undefined | null;
  datasetIds?: IEntityMinimalModel[] | undefined | null;
  formatIds?: IEntityMinimalModel<string>[] | undefined | null;
  customImportIds?: IEntityMinimalModel<string>[] | undefined | null;
  enabled?: { id: boolean | null; name: string } | null;
  types?: { id: DataSource["type"]; name: string }[] | undefined | null;
}

const EnabledFormValues = [
  { id: null, name: "All" },
  { id: true, name: "Enabled" },
  { id: false, name: "Disabled" },
];

const SourceTypeFormValues = DataSourceTypes.map((t) => ({ id: t, name: t }));

const defaultValues: DefinedPropertyObject<DataSourceFilterForm> = {
  bridgeIds: null,
  bridgeTypes: null,
  datasetIds: null,
  formatIds: null,
  customImportIds: null,
  enabled: null,
  types: null,
  ...CombinedModificationRecordEntityDefaultFormValues,
};
export const dataSourcesFormToFilters = (form: DataSourceFilterForm): DataSourceFilters => {
  let filters: DataSourceFilters = {
    ...combinedModificationRecordEntityFormToFilters(form),
  };
  filters.bridgeIds =
    Array.isArray(form.bridgeIds) && form.bridgeIds.length > 0 ? form.bridgeIds.map((d) => d.id) : null;
  filters.bridgeTypes =
    Array.isArray(form.bridgeTypes) && form.bridgeTypes.length > 0 ? form.bridgeTypes.map((d) => d.id) : null;
  filters.datasetIds =
    Array.isArray(form.datasetIds) && form.datasetIds.length > 0 ? form.datasetIds.map((d) => d.id) : null;
  filters.formatIds =
    Array.isArray(form.formatIds) && form.formatIds.length > 0 ? form.formatIds.map((d) => d.id) : null;
  filters.customImportIds =
    Array.isArray(form.customImportIds) && form.customImportIds.length > 0
      ? form.customImportIds.map((d) => d.id)
      : null;
  filters.enabled = form.enabled?.id ?? null;
  filters.types = Array.isArray(form.types) && form.types.length > 0 ? form.types.map((d) => d.id) : null;
  return filters;
};

/**
 * Converts DataSourceFilters to DataSourceFilterForm
 * @param filters DataSourceFilters
 * @returns DataSourceFilterForm
 * @author @CorradoSurmanowicz
 */
export const dataSourcesFiltersToForm = (filters: DataSourceFilters): DataSourceFilterForm => {
  return {
    bridgeIds: filters.bridgeIds ? filters.bridgeIds.map((id) => ({ id, name: "" })) : null,
    bridgeTypes: filters.bridgeTypes ? filters.bridgeTypes.map((id) => ({ id, name: id })) : null,
    datasetIds: filters.datasetIds ? filters.datasetIds.map((id) => ({ id, name: "" })) : null,
    formatIds: filters.formatIds ? filters.formatIds.map((id) => ({ id, name: "" })) : null,
    customImportIds: filters.customImportIds ? filters.customImportIds.map((id) => ({ id, name: "" })) : null,
    enabled:
      filters.enabled !== null ? { id: filters.enabled ?? null, name: filters.enabled ? "Enabled" : "Disabled" } : null,
    types: filters.types ? filters.types.map((id) => ({ id, name: id })) : null,
    ...combinedModificationRecordEntityFiltersToForm(filters),
  };
};

const defaultFilters: DataSourceFilters = {
  enabled: null,
  sortBy: [
    {
      property: "NAME",
      sortDirection: "ASC",
    },
  ],
};

export const DataSourceFilterBar = ({
  initialValues,
  onFormChange,
  dispatchTabStore,
  tabsLoading,
  currentTab,
}: TabStoreFilterSidebarProps<DataSource, DataSourceFilterForm, DataSourceFilters>) => {
  return (
    <TabStoreFilterFormProvider
      tabsLoading={tabsLoading}
      initialValues={initialValues}
      defaultValues={defaultValues}
      defaultFilters={defaultFilters}
      formToFilters={dataSourcesFormToFilters}
      onFormChange={onFormChange}
      dispatchTabStore={dispatchTabStore}
    >
      {({ control, onReset }) => (
        <EntityFilterSideBar onReset={onReset}>
          <BridgesVirtualizedSelectForm id="bridgeIds" label="Bridges" control={control} isMulti />

          <SelectFormField
            id="bridgeTypes"
            label={BridgeFieldLabels.type}
            control={control}
            items={BridgeTypeFormValues}
            placeholder="Select connection type..."
            horizontal={false}
            isMulti
          />

          <DatasetsVirtualizedSelectForm id="datasetIds" label="Datasets" control={control} isMulti />

          <DataFormatsVirtualizedSelectForm id="formatIds" label="Formats" control={control} isMulti />

          <CustomImportsVirtualizedSelectForm
            id="customImportIds"
            label={toUppercase(customImportConstants.entityPlural)}
            control={control}
            isMulti
          />

          {currentTab !== "status" && (
            <SelectFormField
              id="enabled"
              label={DataSourceFieldLabels.enabled}
              control={control}
              horizontal={false}
              items={EnabledFormValues}
              placeholder="Select status..."
            />
          )}

          <SelectFormField
            id="types"
            label={DataSourceFieldLabels.type}
            control={control}
            horizontal={false}
            items={SourceTypeFormValues}
            placeholder="Select source type..."
            isMulti
          />

          <CombinedModificationRecordEntityFilterForm
            control={control}
            currentTab={currentTab}
            initialValues={initialValues}
          />
        </EntityFilterSideBar>
      )}
    </TabStoreFilterFormProvider>
  );
};
